import { TICKET_RESOLUTION_FIELD_LABELS } from '@common/enums';
import type { TicketPolicy, TicketResolutionPayload } from '@common/types';

/** Shared by draft validation and every terminal transition; false is a valid explicit answer. */
export function ticketResolutionErrors(
  resolution: TicketResolutionPayload | undefined,
  policy: TicketPolicy,
  closing: boolean,
  createdAt?: string,
): string[] {
  if (!resolution)
    return closing ? ['Complete the resolution form before closing this ticket.'] : [];
  const errors: string[] = [];
  if (closing)
    for (const key of policy.requiredResolutionFields) {
      const value = resolution[key];
      if (
        value == null ||
        (typeof value === 'string' &&
          !value
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim())
      )
        errors.push(`Required: ${TICKET_RESOLUTION_FIELD_LABELS[key]}.`);
    }
  const first = resolution.dateOfFirstReply;
  const end = resolution.dateOfResolution;
  for (const date of [first, end]) {
    if (
      date &&
      (!Number.isFinite(Date.parse(date)) ||
        Date.parse(date) > Date.now() ||
        (createdAt && Date.parse(date) < Date.parse(createdAt)))
    )
      errors.push('Resolution dates must be valid, after creation and no later than now.');
  }
  if (first && end && Date.parse(end) < Date.parse(first))
    errors.push('Resolution must be on or after the first reply.');
  if (resolution.compensationGiven) {
    if (!resolution.compensationTypes.length) errors.push('Select a compensation type.');
    if (!/^[A-Z]{3}$/.test(resolution.currency ?? ''))
      errors.push('Enter a three-letter currency code.');
    for (const type of resolution.compensationTypes) {
      const value = resolution.compensationValues[type];
      if (
        value == null ||
        !Number.isFinite(value) ||
        value < 0 ||
        ((type === 'refund' || type === 'evoucher') && value === 0)
      )
        errors.push(
          `Enter a valid ${type} amount (refunds and vouchers must be greater than zero).`,
        );
    }
    if (resolution.compensationTypes.includes('evoucher') && !resolution.voucherReference?.trim())
      errors.push('Enter the voucher reference.');
  }
  return [...new Set(errors)];
}
